import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { JobBoardRequisition } from '@/types'
import { getJobBoardItem, updateJobBoardItem } from '@/lib/api'
import { Card } from '@/components/ui/Card'
import { TextField, TextAreaField, SelectField, SaveBar } from '@/components/ui/FormFields'
import { PendingBackendNotice } from '@/components/ui/EmptyState'

const STATUSES = ['borrador', 'en_revision', 'aprobado', 'en_construccion', 'desplegado']

export function JobBoardDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [req, setReq] = useState<JobBoardRequisition | null>(null)
  const [tab, setTab] = useState<'jd' | 'spec'>('jd')
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    getJobBoardItem(id).then(setReq)
  }, [id])

  if (!req) return <div style={{ color: 'var(--text-secondary)' }}>Cargando…</div>

  function update<K extends keyof JobBoardRequisition>(key: K, value: JobBoardRequisition[K]) {
    setReq((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  async function handleSave() {
    if (!req) return
    setSaving(true)
    setResult(null)
    try {
      await updateJobBoardItem(req.id, req)
      setResult('Guardado — solo en esta sesión (sin backend conectado)')
    } catch (e) {
      setResult(e instanceof Error ? e.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <PendingBackendNotice endpoint={`PATCH /job-board/${req.id}`} />
      <button onClick={() => navigate('/job-board')} style={backLinkStyle}>← Volver a Job Board</button>
      <h1 style={{ fontSize: 20, fontWeight: 500, marginBottom: 16 }}>{req.agent_name}</h1>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <TextField label="Nombre del agente propuesto" value={req.agent_name} onChange={(v) => update('agent_name', v)} />
          <SelectField
            label="Estado"
            value={req.status}
            onChange={(v) => update('status', v as JobBoardRequisition['status'])}
            options={STATUSES}
          />
        </div>
        <TextField label="Owner" value={req.owner ?? ''} onChange={(v) => update('owner', v)} />
      </Card>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
        {(['jd', 'spec'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 14px',
              fontSize: 13,
              color: tab === t ? 'var(--text-primary)' : 'var(--text-secondary)',
              borderBottom: tab === t ? '2px solid var(--status-hitl)' : '2px solid transparent',
              marginBottom: -1,
              cursor: 'pointer',
            }}
          >
            {t === 'jd' ? 'JD (Job Description)' : 'Spec técnica'}
          </button>
        ))}
      </div>

      <Card>
        {tab === 'jd' ? (
          <TextAreaField
            label="JD — qué problema resuelve, responsabilidades, límites de scope, tools que necesita, criterios de éxito"
            value={req.jd ?? ''}
            onChange={(v) => update('jd', v)}
            rows={14}
          />
        ) : (
          <TextAreaField
            label="Spec — detalle técnico para builders (archivos de definición, tools.yaml, permissions.yaml)"
            value={req.spec ?? ''}
            onChange={(v) => update('spec', v)}
            rows={14}
          />
        )}
      </Card>

      <SaveBar onSave={handleSave} saving={saving} result={result} />
    </div>
  )
}

const backLinkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--text-secondary)',
  fontSize: 13,
  cursor: 'pointer',
  padding: 0,
  marginBottom: 12,
  display: 'block',
}
